"use client"

import dynamic from "next/dynamic"
import { LogIn, LogOut, Menu, Settings, UserRound } from "lucide-react"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"
import { Logo } from "@/shared/components/logo"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { useUIStore } from "@/shared/stores/ui-store"
import type { AuthModalContext } from "@/shared/stores/ui-store"
import { useAuth } from "@/features/auth/hooks/use-auth"
import { ROUTES } from "@/shared/lib/constants"
import { CoinsDisplay } from "@/features/credits/components/credits-display"

const MobileNav = dynamic(() =>
  import("./mobile-nav").then((module) => module.MobileNav)
)

interface TopbarProps {
  title?: string
  authModalContext?: AuthModalContext | null
}

export function Topbar({ title, authModalContext = null }: TopbarProps) {
  const t = useTranslations("chrome")
  const { mobileNavOpen, openSettings, setMobileNavOpen } = useUIStore()
  const { user, profile, isAuthenticated, isLoading, logout } = useAuth()
  // Only show a letter when we have a username; otherwise show placeholder to avoid letter flash (e.g. A → J)
  const initial =
    profile?.username != null && profile.username.length > 0
      ? (profile.username.charAt(0).toUpperCase() || "?")
      : null

  // Anonymous auth CTAs navigate to the dedicated /login and /register pages
  // (same behavior as the hero and the rest of the homepage) instead of opening
  // a modal with no URL change, which read as "nothing happened". Preserve any
  // character context (e.g. on a chat page) so the auth page can stay character-aware.
  const characterQuery = authModalContext?.characterId
    ? `?characterId=${encodeURIComponent(authModalContext.characterId)}`
    : ""
  const loginHref = `${ROUTES.login}${characterQuery}`
  const registerHref = `${ROUTES.register}${characterQuery}`

  return (
    <>
      <header
        className={cn(
          "flex h-14 items-center gap-1 px-2 backdrop-blur-md min-[380px]:gap-2 min-[380px]:px-3 sm:h-16 sm:gap-4 sm:px-4 lg:px-6 min-w-0 overflow-x-hidden",
          "bg-background/80"
        )}
      >
        {/* Left: Mobile menu + Logo (mobile only) + title */}
        <div className="flex items-center gap-1 min-w-0 flex-1 min-[380px]:gap-2 sm:gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-foreground shrink-0 max-[379px]:size-8"
            onClick={() => setMobileNavOpen(true)}
            aria-label={t("actions.openMenu")}
          >
            <Menu className="size-5" />
            <span className="sr-only">{t("actions.openMenu")}</span>
          </Button>

          {/* Logo shown on mobile only (sidebar is hidden) */}
          <LocalizedLink href="/" className="lg:hidden shrink-0">
            <Logo size="sm" responsive />
          </LocalizedLink>

          {title && (
            <h2 className="hidden sm:block text-base sm:text-lg font-semibold text-foreground truncate min-w-0">
              {title}
            </h2>
          )}
        </div>

        {/* Right: Actions */}
        <div className="ml-auto flex items-center gap-1 sm:gap-2 shrink-0">
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
          ) : isAuthenticated && user ? (
            <>
              {/* Credits display / Upgrade button — only when profile loaded to avoid Upgrade → credits flash */}
              {profile ? (
                <CoinsDisplay />
              ) : (
                <div className="h-8 min-w-[4.75rem] sm:min-w-[5.25rem] rounded-full bg-muted/80 animate-pulse" aria-hidden />
              )}

              {/* FIX: PostHog showed rage clicks on notification bell — hidden until feature exists */}

              {/* Account menu: first letter of username only (incognito); TikTok-style minimal avatar */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      // Sora-spec avatar trigger: flat subtle surface, hairline,
                      // no gradient or shadow, single-pixel focus outline.
                      "flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold",
                      "bg-foreground/[0.06] text-foreground border border-border",
                      "hover:bg-foreground/[0.10] transition-colors",
                      "outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    )}
                    aria-label={t("actions.accountMenu")}
                  >
                    {initial !== null ? (
                      initial
                    ) : (
                      <span className="size-4 rounded-full bg-muted/80 animate-pulse" aria-hidden />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-52 p-1.5"
                >
                  {profile?.username ? (
                    <DropdownMenuLabel className="px-2.5 pb-2 pt-1.5 text-[11px] font-normal text-muted-foreground normal-case">
                      <span className="sr-only">Signed in as </span>
                      <span className="block truncate font-medium text-foreground text-sm">
                        @{profile.username}
                      </span>
                    </DropdownMenuLabel>
                  ) : null}
                  {profile?.username && (
                    <DropdownMenuItem asChild className="rounded-lg py-2">
                      <LocalizedLink
                        href={ROUTES.profileByUsername(profile.username)}
                        className="cursor-pointer gap-2.5"
                        prefetch={false}
                      >
                        <UserRound className="size-4 text-muted-foreground" />
                        {t("nav.profile")}
                      </LocalizedLink>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="cursor-pointer rounded-lg py-2 gap-2.5"
                    onClick={() => openSettings("account", { source: "navigation" })}
                  >
                    <Settings className="size-4 text-muted-foreground" />
                    {t("nav.settings")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1.5 bg-border/80" />
                  <DropdownMenuItem
                    variant="destructive"
                    className="cursor-pointer rounded-lg py-2 gap-2.5"
                    onClick={() => logout()}
                  >
                    <LogOut className="size-4" />
                    {t("actions.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                asChild
                className="text-muted-foreground hover:text-foreground min-[400px]:hidden"
                aria-label={t("actions.login")}
              >
                <LocalizedLink href={loginHref}>
                  <LogIn className="size-4" />
                  <span className="sr-only">{t("actions.login")}</span>
                </LocalizedLink>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="hidden h-8 gap-1.5 px-3 text-xs font-medium text-muted-foreground hover:text-foreground min-[400px]:inline-flex sm:h-9 sm:px-4 sm:text-sm"
              >
                <LocalizedLink href={loginHref}>{t("actions.login")}</LocalizedLink>
              </Button>
              <Button
                size="sm"
                asChild
                className="h-8 gap-1.5 px-2 text-xs font-semibold min-[380px]:px-3 sm:h-9 sm:px-4 sm:text-sm"
              >
                <LocalizedLink href={registerHref}>{t("actions.joinFree")}</LocalizedLink>
              </Button>
            </>
          )}
        </div>
      </header>

      {/* Mobile nav sheet */}
      {mobileNavOpen && (
        <MobileNav
          open={mobileNavOpen}
          onOpenChange={setMobileNavOpen}
          authModalContext={authModalContext}
        />
      )}
    </>
  )
}
